// src/providers/msessaging.jsx

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from 'lib/auth';
import { useNotificationStore } from 'stores/notifications';
import { MESSAGING_SOCKET_URL } from 'config';
import storage from 'utils/storage';
import { queryClient } from 'lib/react-query';

// Create context
const MessagingSocketContext = createContext(null);
export const useMessagingSocket = () => useContext(MessagingSocketContext);

export const MessagingSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const { addNotification } = useNotificationStore();

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessage, setLastMessage] = useState(null);

  const reconnectRef = useRef(false);
  const token = storage.getToken();

  /** 🔌 Initialize Socket */
  const initializeSocket = useCallback(() => {
    if (!token) return;

    const path =
      process.env.REACT_APP_SOCKET_ENV === 'development' ? '/socket.io' : '/messaging/socket.io';

    const cleanToken = token.replace(/^Bearer\s+/i, '');
    const socketInstance = io(MESSAGING_SOCKET_URL, {
      auth: { token: cleanToken },
      path,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    // ✅ Connection events
    socketInstance.on('connect', () => {
      console.log('✅ Socket connected:', socketInstance.id);
      setConnected(true);
      // addNotification({ type: 'success', title: 'Connected to messaging service' });
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setConnected(false);
      addNotification({ type: 'warning', title: 'Disconnected from messaging service' });
    });

    socketInstance.on('connect_error', (err) => {
      console.error('⚠️ Socket connection error:', err);
      addNotification({
        type: 'error',
        title: 'Socket connection failed',
        message: err.message,
      });
      setConnected(false);
    });

    /** 👥 Presence updates */
    socketInstance.on('presence:update', (data) => {
      setPresence((prev) => ({
        ...prev,
        [data.userId]: data.online,
      }));
    });

    /** ✍️ Typing indicators */
    socketInstance.on('typing:start', (data) => {
      setTypingUsers((prev) => {
        const existing = prev[data.conversationId] || [];
        if (existing.includes(data.userName)) return prev;
        return {
          ...prev,
          [data.conversationId]: [...existing, data.userName],
        };
      });
    });

    socketInstance.on('typing:stop', (data) => {
      setTypingUsers((prev) => {
        const updated = { ...prev };
        if (updated[data.conversationId]) {
          updated[data.conversationId] = updated[data.conversationId].filter(
            (u) => u !== data.userName
          );
        }
        return updated;
      });
    });

    /** 💬 Message Events */
    socketInstance.on('message:new', (msg) => {
      console.log('📩 message:new', msg);
      setLastMessage(msg);

      // ✅ Update messages cache
      queryClient.setQueriesData([msg.conversationId, 'conversation_messages'], (old) => {
        if (!old?.messages) return { messages: [msg] };
        return { ...old, messages: [...old.messages, msg] };
      });

      // ✅ Update sidebar conversations cache
      queryClient.setQueriesData(['conversations'], (old) => {
        if (!old?.conversations) return old;
        const updated = old.conversations.map((c) =>
          c._id === msg.conversationId
            ? {
                ...c,
                lastMessage: {
                  content: msg.content || msg.ciphertext || '[encrypted]',
                  createdAt: msg.createdAt,
                },
              }
            : c
        );

        // Move conversation to top
        updated.sort(
          (a, b) =>
            new Date(b.lastMessage?.createdAt || 0) - new Date(a.lastMessage?.createdAt || 0)
        );

        return { ...old, conversations: updated };
      });

      // ✅ Increment unread if not from me
      if (msg.senderId !== user?._id) {
        setUnreadCounts((prev) => ({
          ...prev,
          [msg.conversationId]: (prev[msg.conversationId] || 0) + 1,
        }));
      }
    });

    socketInstance.on('message:read', (data) => {
      console.log('📖 message:read', data);
      setUnreadCounts((prev) => ({
        ...prev,
        [data.conversationId]: 0,
      }));
    });

    setSocket(socketInstance);
    reconnectRef.current = true;

    return () => socketInstance.disconnect();
  }, [token, addNotification, user]);

  /** 🔁 Reconnect when token available */
  useEffect(() => {
    if (token) initializeSocket();
    return () => socket?.disconnect();
  }, [token]);

  /** 🚀 Emit message */
  const emitMessage = useCallback(
    (payload, callback) => {
      if (!socket || !connected) {
        console.warn('Socket not connected');
        return;
      }

      const optimisticMsg = {
        _id: `temp-${Date.now()}`,
        conversationId: payload.conversationId,
        senderId: user?._id,
        content: payload.ciphertext,
        createdAt: new Date().toISOString(),
      };

      // Optimistically update messages
      queryClient.setQueriesData([payload.conversationId, 'conversation_messages'], (old) => {
        if (!old?.messages) return { messages: [optimisticMsg] };
        return { ...old, messages: [...old.messages, optimisticMsg] };
      });

      // Optimistically update sidebar
      queryClient.setQueriesData(['conversations'], (old) => {
        if (!old?.conversations) return old;
        const updated = old.conversations.map((c) =>
          c._id === payload.conversationId
            ? {
                ...c,
                lastMessage: {
                  content: optimisticMsg.content,
                  createdAt: optimisticMsg.createdAt,
                },
              }
            : c
        );
        return { ...old, conversations: updated };
      });

      socket.emit('message:send', payload, (ack) => {
        if (ack?.success) {
          console.log('✅ message sent', ack);
          queryClient.invalidateQueries([payload.conversationId, 'conversation_messages']);
          queryClient.invalidateQueries(['conversations']);
        } else {
          console.warn('❌ message send failed', ack);
        }
        if (callback) callback(ack);
      });
    },
    [socket, connected, user]
  );

  /** ✍️ Typing events */
  const emitTypingStart = useCallback(
    (conversationId) => {
      if (!socket || !connected) return;
      socket.emit('typing:start', {
        conversationId,
        userName: user?.firstName || user?.username || 'You',
      });
    },
    [socket, connected, user]
  );

  const emitTypingStop = useCallback(
    (conversationId) => {
      if (!socket || !connected) return;
      socket.emit('typing:stop', {
        conversationId,
        userName: user?.firstName || user?.username || 'You',
      });
    },
    [socket, connected, user]
  );

  /** 📖 Mark conversation as read */
  const markConversationRead = useCallback(
    (conversationId) => {
      if (!socket || !connected) return;
      socket.emit('message:read', { conversationId });
      setUnreadCounts((prev) => ({ ...prev, [conversationId]: 0 }));
    },
    [socket, connected]
  );

  const value = {
    socket,
    connected,
    presence,
    typingUsers,
    unreadCounts,
    lastMessage,
    emitMessage,
    emitTypingStart,
    emitTypingStop,
    markConversationRead,
  };

  return (
    <MessagingSocketContext.Provider value={value}>{children}</MessagingSocketContext.Provider>
  );
};
